import {
  type FormEvent,
  type KeyboardEvent,
  type ClipboardEvent,
  useState,
  useEffect,
  useRef,
  useCallback,
} from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Circle,
  Lock,
  Mail,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { Input } from '../components/ui/Input';
import {
  solicitarResetPassword,
  verificarResetPin,
  confirmarResetPassword,
} from '../services/authService';
import styles from './ResetPasswordPage.module.css';

const PIN_LENGTH = 6;
const RESEND_COOLDOWN_S = 60;
const SUCCESS_REDIRECT_S = 5;

interface PasswordRequirement {
  id: string;
  label: string;
  test: (pw: string) => boolean;
}

const REQUIREMENTS: PasswordRequirement[] = [
  { id: 'len',   label: 'Mínimo 8 caracteres',     test: (pw) => pw.length >= 8 },
  { id: 'upper', label: 'Una mayúscula',           test: (pw) => /[A-Z]/.test(pw) },
  { id: 'lower', label: 'Una minúscula',           test: (pw) => /[a-z]/.test(pw) },
  { id: 'digit', label: 'Un número',               test: (pw) => /\d/.test(pw) },
  { id: 'spec',  label: 'Un carácter especial',    test: (pw) => /[!@#$%^&*()_+\-=[\]{}|;:,.<>?]/.test(pw) },
];

type Phase = 'request' | 'pin' | 'newPassword' | 'success';

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function passwordStrength(pw: string): 0 | 1 | 2 | 3 {
  if (!pw) return 0;
  const score = REQUIREMENTS.reduce((acc, r) => acc + (r.test(pw) ? 1 : 0), 0);
  if (score <= 2) return 1;
  if (score <= 4) return 2;
  return 3;
}

function strengthLabel(level: 0 | 1 | 2 | 3): string {
  if (level === 0) return '';
  if (level === 1) return 'Débil';
  if (level === 2) return 'Media';
  return 'Fuerte';
}

function extractAuthError(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    const msg = err.message;
    // Firebase Functions wraps errors — surface the inner message if present
    if (msg.includes('Demasiados') || msg.includes('rate')) {
      return 'Demasiados intentos. Espere unos minutos e intente nuevamente.';
    }
    if (msg.includes('expirado') || msg.includes('expired')) {
      return 'El código ha expirado. Solicite uno nuevo.';
    }
    if (msg.includes('incorrecto') || msg.includes('invalid')) {
      return 'Código incorrecto. Verifique e intente nuevamente.';
    }
    if (msg.includes('contraseña') || msg.includes('password')) {
      return msg;
    }
    // «internal» es lo que pone Firebase ante un fallo no controlado: no dice nada al usuario.
    if (/^internal$/i.test(msg.trim())) return fallback;
    return msg || fallback;
  }
  return fallback;
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();

  const [phase, setPhase] = useState<Phase>('request');
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [pin, setPin] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [password, setPassword] = useState('');
  const [confirmar, setConfirmar] = useState('');
  const [error, setError] = useState('');
  const [pinError, setPinError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [shake, setShake] = useState(false);
  const [redirectCountdown, setRedirectCountdown] = useState(SUCCESS_REDIRECT_S);

  const pinRefs = useRef<(HTMLInputElement | null)[]>([]);
  const emailDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── Email validation (debounced) ──────────────────────────

  const validateEmail = useCallback((value: string) => {
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);
    emailDebounceRef.current = setTimeout(() => {
      if (value && !isValidEmail(value)) {
        setEmailError('Formato de correo inválido');
      } else {
        setEmailError('');
      }
    }, 400);
  }, []);

  // ─── Cooldown ticker ───────────────────────────────────────

  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // ─── Success redirect countdown ────────────────────────────

  useEffect(() => {
    if (phase !== 'success') return;
    if (redirectCountdown <= 0) {
      navigate('/', { replace: true });
      return;
    }
    const timer = setTimeout(() => setRedirectCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [phase, redirectCountdown, navigate]);

  // ─── PIN handlers ──────────────────────────────────────────

  const fullPin = pin.join('');

  const handleAutoVerify = useCallback(
    async (currentPin: string) => {
      if (currentPin.length !== PIN_LENGTH || verifying) return;

      setVerifying(true);
      setPinError('');

      try {
        await verificarResetPin(email.trim().toLowerCase(), currentPin);
        setPhase('newPassword');
      } catch (err) {
        const msg = extractAuthError(err, 'Código incorrecto.');
        setPinError(msg);
        setShake(true);
        setTimeout(() => setShake(false), 500);
        setPin(Array(PIN_LENGTH).fill(''));
        setTimeout(() => pinRefs.current[0]?.focus(), 100);
      } finally {
        setVerifying(false);
      }
    },
    [email, verifying],
  );

  useEffect(() => {
    if (fullPin.length === PIN_LENGTH) {
      handleAutoVerify(fullPin);
    }
  }, [fullPin, handleAutoVerify]);

  const handlePinChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;
    const newPin = [...pin];
    newPin[index] = value.slice(-1);
    setPin(newPin);
    setPinError('');
    if (value && index < PIN_LENGTH - 1) {
      pinRefs.current[index + 1]?.focus();
    }
  };

  const handlePinKeyDown = (
    index: number,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      const newPin = [...pin];
      newPin[index - 1] = '';
      setPin(newPin);
      pinRefs.current[index - 1]?.focus();
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) pinRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1)
      pinRefs.current[index + 1]?.focus();
  };

  const handlePinPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, PIN_LENGTH);
    if (!pasted) return;
    const newPin = Array(PIN_LENGTH).fill('');
    pasted.split('').forEach((char, i) => {
      newPin[i] = char;
    });
    setPin(newPin);
    pinRefs.current[Math.min(pasted.length, PIN_LENGTH - 1)]?.focus();
  };

  // ─── Phase: Request ────────────────────────────────────────

  const canSubmitRequest =
    email.trim() !== '' && !emailError && !submitting;

  const handleRequestSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (emailDebounceRef.current) clearTimeout(emailDebounceRef.current);

    if (!isValidEmail(email)) {
      setEmailError('Formato de correo inválido');
      return;
    }
    if (!canSubmitRequest) return;

    setSubmitting(true);
    setError('');

    try {
      await solicitarResetPassword(email.trim().toLowerCase());
      setPhase('pin');
      setCooldown(RESEND_COOLDOWN_S);
      setTimeout(() => pinRefs.current[0]?.focus(), 200);
    } catch (err) {
      // Antes se simulaba el éxito (se pasaba a la pantalla del código) para no revelar
      // si el correo existía. El usuario quedaba esperando indefinidamente un código que
      // nunca se generó. Ahora el servidor devuelve el motivo y se muestra tal cual.
      setError(extractAuthError(err, 'No se pudo enviar el código. Intente nuevamente.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0 || submitting) return;
    setSubmitting(true);
    setPinError('');
    try {
      await solicitarResetPassword(email.trim().toLowerCase());
      setCooldown(RESEND_COOLDOWN_S);
      setPin(Array(PIN_LENGTH).fill(''));
      pinRefs.current[0]?.focus();
    } catch (err) {
      setPinError(extractAuthError(err, 'Error al reenviar el código.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToRequest = () => {
    setPhase('request');
    setPin(Array(PIN_LENGTH).fill(''));
    setPinError('');
    setError('');
    setCooldown(0);
  };

  // ─── Phase: New password ───────────────────────────────────

  const strength = passwordStrength(password);
  const allReqsMet = REQUIREMENTS.every((r) => r.test(password));
  const passwordsMatch = password === confirmar && confirmar.length > 0;
  const canSubmitNew = allReqsMet && passwordsMatch && !submitting;

  const handleNewPasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmitNew) return;

    setSubmitting(true);
    setError('');

    try {
      await confirmarResetPassword(
        email.trim().toLowerCase(),
        fullPin,
        password,
      );
      setPhase('success');
      setRedirectCountdown(SUCCESS_REDIRECT_S);
    } catch (err) {
      const msg = extractAuthError(err, 'Error al cambiar la contraseña.');
      // If the PIN expired during password entry, send back to PIN phase
      if (msg.toLowerCase().includes('expirado') || msg.toLowerCase().includes('código')) {
        setPinError(msg);
        setPin(Array(PIN_LENGTH).fill(''));
        setPassword('');
        setConfirmar('');
        setPhase('pin');
        setTimeout(() => pinRefs.current[0]?.focus(), 200);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackToPin = () => {
    setPassword('');
    setConfirmar('');
    setError('');
    setPin(Array(PIN_LENGTH).fill(''));
    setPhase('pin');
    setTimeout(() => pinRefs.current[0]?.focus(), 100);
  };

  // ─── Step indicator helper ─────────────────────────────────

  const stepNumber =
    phase === 'request' ? 1 : phase === 'pin' ? 2 : phase === 'newPassword' ? 3 : 3;

  // ─── Render ────────────────────────────────────────────────

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        <Link to="/" className={styles.brand} aria-label="Volver al inicio de sesión">
          <img className={styles.brandLogo} src="/minsalud-logo.png" alt="MINSAlud — Portal del Paciente" />
        </Link>

        {/* ─── Step indicator (hidden in success) ─── */}
        {phase !== 'success' && (
          <div className={styles.stepper} aria-label={`Paso ${stepNumber} de 3`}>
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`${styles.step} ${
                  n < stepNumber
                    ? styles.stepDone
                    : n === stepNumber
                      ? styles.stepActive
                      : ''
                }`}
              >
                <span className={styles.stepDot}>
                  {n < stepNumber ? <CheckCircle2 size={14} /> : n}
                </span>
                <span className={styles.stepLabel}>
                  {n === 1 && 'Correo'}
                  {n === 2 && 'Código'}
                  {n === 3 && 'Nueva clave'}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* ─── Phase: Request email ─── */}
        {phase === 'request' && (
          <div className={styles.phaseBody}>
            <span className={styles.eyebrow}>
              <span className={styles.editorialNum}>01</span>
              <span className={styles.eyebrowText}>Recuperación de acceso</span>
            </span>
            <h1 className={styles.title}>
              ¿Olvidó su <em>contraseña</em>?
            </h1>
            <p className={styles.text}>
              Ingrese su correo institucional. Si está registrado, le enviaremos
              un código de 6 dígitos para restablecer su contraseña.
            </p>
            <form className={styles.form} onSubmit={handleRequestSubmit} noValidate>
              <Input
                label="Correo institucional"
                type="email"
                placeholder="correo@ejemplo.com"
                leftIcon={<Mail size={16} />}
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setError('');
                  validateEmail(e.target.value);
                }}
                error={emailError || undefined}
                disabled={submitting}
                autoComplete="email"
                autoFocus
                required
              />
              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!canSubmitRequest}
              >
                {submitting ? (
                  'Enviando...'
                ) : (
                  <>
                    <Send size={16} />
                    Enviar código
                  </>
                )}
              </button>
              {error && (
                <div className={styles.errorAlert} role="alert">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}
            </form>

            <Link to="/" className={styles.backLink}>
              <ArrowLeft size={14} /> Volver al inicio de sesión
            </Link>
          </div>
        )}

        {/* ─── Phase: Enter PIN ─── */}
        {phase === 'pin' && (
          <div className={styles.phaseBody}>
            <div className={styles.pinIconWrap} aria-hidden="true">
              <div className={styles.pinIcon}>
                <ShieldCheck size={32} />
              </div>
            </div>
            <h1 className={styles.title}>
              Ingrese el <em>código</em>
            </h1>
            <p className={styles.text}>
              Enviamos un código de 6 dígitos a<br />
              <strong>{email}</strong>
              <br />
              <span className={styles.expiryHint}>Válido por 10 minutos</span>
            </p>

            <div
              className={`${styles.pinContainer} ${shake ? styles.pinShake : ''}`}
              role="group"
              aria-label="Código de 6 dígitos"
            >
              {Array.from({ length: PIN_LENGTH }).map((_, i) => (
                <input
                  key={i}
                  ref={(el) => {
                    pinRefs.current[i] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={pin[i]}
                  onChange={(e) => handlePinChange(i, e.target.value)}
                  onKeyDown={(e) => handlePinKeyDown(i, e)}
                  onPaste={i === 0 ? handlePinPaste : undefined}
                  onFocus={(e) => e.target.select()}
                  className={`${styles.pinInput} ${pin[i] ? styles.pinFilled : ''} ${
                    pinError ? styles.pinErrorInput : ''
                  }`}
                  disabled={verifying}
                  autoComplete="one-time-code"
                  aria-label={`Dígito ${i + 1}`}
                />
              ))}
            </div>

            {verifying && (
              <p className={styles.verifyingText} role="status">
                Verificando código…
              </p>
            )}

            {pinError && (
              <div className={styles.errorAlert} role="alert">
                <AlertCircle size={18} />
                <span>{pinError}</span>
              </div>
            )}

            <div className={styles.resendRow}>
              <span className={styles.resendLabel}>¿No recibió el código?</span>
              <button
                type="button"
                className={styles.resendBtn}
                onClick={handleResend}
                disabled={cooldown > 0 || submitting || verifying}
              >
                {cooldown > 0 ? `Reenviar en ${cooldown}s` : 'Reenviar código'}
              </button>
            </div>

            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleBackToRequest}
              >
                <ArrowLeft size={14} /> Usar otro correo
              </button>
            </div>
          </div>
        )}

        {/* ─── Phase: New password ─── */}
        {phase === 'newPassword' && (
          <div className={styles.phaseBody}>
            <div className={styles.pinIconWrap} aria-hidden="true">
              <div className={styles.pinIconSuccess}>
                <CheckCircle2 size={32} />
              </div>
            </div>
            <h1 className={styles.title}>
              Nueva <em>contraseña</em>
            </h1>
            <p className={styles.text}>
              Código verificado. Cree una contraseña robusta para
              <br /><strong>{email}</strong>.
            </p>

            <form
              className={styles.form}
              onSubmit={handleNewPasswordSubmit}
              noValidate
            >
              <Input
                label="Nueva contraseña"
                type="password"
                placeholder="Mínimo 8 caracteres"
                leftIcon={<Lock size={16} />}
                showPasswordToggle
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setError('');
                }}
                disabled={submitting}
                autoComplete="new-password"
                autoFocus
                required
              />

              {password.length > 0 && (
                <>
                  <div className={styles.strengthBar} aria-hidden="true">
                    <div
                      className={styles.strengthFill}
                      data-level={strength}
                      style={{ width: `${(strength / 3) * 100}%` }}
                    />
                    <span
                      className={styles.strengthLabel}
                      data-level={strength}
                    >
                      {strengthLabel(strength)}
                    </span>
                  </div>

                  <ul className={styles.requirements} aria-label="Requisitos de contraseña">
                    {REQUIREMENTS.map((r) => {
                      const met = r.test(password);
                      return (
                        <li
                          key={r.id}
                          className={met ? styles.reqMet : styles.reqUnmet}
                        >
                          {met ? (
                            <CheckCircle2 size={13} aria-hidden="true" />
                          ) : (
                            <Circle size={13} aria-hidden="true" />
                          )}
                          {r.label}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}

              <Input
                label="Confirmar contraseña"
                type="password"
                placeholder="Repita su contraseña"
                leftIcon={<Lock size={16} />}
                showPasswordToggle
                value={confirmar}
                onChange={(e) => {
                  setConfirmar(e.target.value);
                  setError('');
                }}
                error={
                  confirmar.length > 0 && !passwordsMatch
                    ? 'Las contraseñas no coinciden'
                    : undefined
                }
                disabled={submitting}
                autoComplete="new-password"
                required
              />

              <button
                type="submit"
                className={styles.submitBtn}
                disabled={!canSubmitNew}
              >
                {submitting ? (
                  'Cambiando…'
                ) : (
                  <>
                    Cambiar contraseña
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              {error && (
                <div className={styles.errorAlert} role="alert">
                  <AlertCircle size={18} />
                  <span>{error}</span>
                </div>
              )}
            </form>

            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.linkBtn}
                onClick={handleBackToPin}
              >
                <ArrowLeft size={14} /> Ingresar otro código
              </button>
            </div>
          </div>
        )}

        {/* ─── Phase: Success ─── */}
        {phase === 'success' && (
          <div className={styles.phaseBody}>
            <div className={styles.pinIconWrap} aria-hidden="true">
              <div className={styles.pinIconSuccess}>
                <CheckCircle2 size={32} />
              </div>
            </div>
            <h1 className={styles.title}>
              Contraseña <em>actualizada</em>
            </h1>
            <p className={styles.text}>
              Su contraseña ha sido cambiada con éxito. Ya puede iniciar sesión
              con sus nuevas credenciales.
            </p>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={() => navigate('/', { replace: true })}
            >
              Ir al inicio de sesión
              <ArrowRight size={16} />
            </button>
            <p className={styles.countdown} aria-live="polite">
              Redirigiendo en <strong>{redirectCountdown}s</strong>…
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
